import React from "react";
import { Link } from "react-router-dom";
import HUDFrame from "../components/HUDFrame";
import PlatformShowcase from "../components/PlatformShowcase";
import IndustriesSection from "../components/IndustriesSection";
import {
  StrainRecognitionIcon,
  PotencyInsightsIcon,
  QualityAssessmentIcon,
  BatchTrackingIcon,
  EducationIcon,
  ComplianceIcon,
} from "../components/FeatureIcons";
import { Lock, ChevronsRight, Check } from "lucide-react";

const FEATURES = [
  {
    Icon: StrainRecognitionIcon,
    title: "PRODUCT\nRECOGNITION",
    desc: "Instantly identify plant varieties and product SKUs using advanced image analysis.",
    href: "/industries/agriculture",
  },
  {
    Icon: PotencyInsightsIcon,
    title: "COMPOSITION\nINSIGHTS",
    desc: "Estimate potency markers, terpene profiles, and quality composition for smarter inventory.",
    href: "/industries/food-distribution",
  },
  {
    Icon: QualityAssessmentIcon,
    title: "QUALITY\nASSESSMENT",
    desc: "Detect mold, mildew, pests, and other quality concerns instantly.",
    href: "/industries/restaurants",
  },
  {
    Icon: BatchTrackingIcon,
    title: "BATCH\nTRACKING",
    desc: "Track and compare batches for consistency and compliance.",
    href: "/industries/supply-chain",
  },
  {
    Icon: EducationIcon,
    title: "INSIGHTS &\nGUIDANCE",
    desc: "Access product data, quality markers, and vertical-specific operational guidance.",
    href: "/about",
  },
  {
    Icon: ComplianceIcon,
    title: "COMPLIANCE\nREADY",
    desc: "Generate reports and maintain records with ease for compliance.",
    href: "/industries/supply-chain",
  },
];

export default function Home({ onRequestDemo }) {
  return (
    <div className="relative z-10">
      {/* HERO — official custom brand collage (sprout-in-scanner + wordmark + MCT signature) */}
      <section className="pt-24 pb-8 px-6" data-testid="home-hero">
        <div className="max-w-[1280px] mx-auto flex justify-center">
          <div className="ss-hero-frame">
            <span className="ss-hero-corner ss-hero-corner--tl" />
            <span className="ss-hero-corner ss-hero-corner--tr" />
            <span className="ss-hero-corner ss-hero-corner--bl" />
            <span className="ss-hero-corner ss-hero-corner--br" />
            <span className="ss-hero-scanline" aria-hidden="true" />
            <div className="ss-hero-logo">
              <img
                src="/hero-logo.png"
                alt="StrainScan Pro — Powered by Medical Care Technologies Inc."
                className="ss-hero-logo__img"
                width="1402"
                height="1122"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM REACH — broader industries (elevated to the top) */}
      <IndustriesSection />

      {/* PLATFORM IN MOTION — animated software-function showcase */}
      <PlatformShowcase />

      {/* AI-POWERED banner — moved lower, generic language */}
      <section className="pt-2 px-6 pb-10">
        <div className="max-w-[1280px] mx-auto">
          <HUDFrame padding="p-8 md:p-10">
            <div className="text-center">
              <h1
                className="font-display font-black"
                style={{
                  fontSize: "clamp(28px, 4.5vw, 56px)",
                  letterSpacing: "0.05em",
                }}
              >
                <span style={{
                  background: "linear-gradient(180deg,#ffffff,#cfcfcf)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>
                  AI-POWERED AGRICULTURAL INTELLIGENCE
                </span>
              </h1>
              <h2
                className="text-green glow font-display font-black mt-2"
                style={{
                  fontSize: "clamp(26px, 4.2vw, 52px)",
                  letterSpacing: "0.08em",
                }}
              >
                FOR EVERY VERTICAL.
              </h2>
            </div>

            {/* 6-feature column row */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {FEATURES.map((f, i) => (
                <Link
                  to={f.href}
                  key={f.title}
                  className="group flex flex-col items-center text-center px-2"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="w-24 h-24 border border-[rgba(157,252,4,0.45)] grid place-items-center transition group-hover:border-[var(--ss-green)] group-hover:bg-[rgba(157,252,4,0.06)]">
                    <f.Icon size={68} />
                  </div>
                  <div
                    className="mt-4 font-display font-bold text-[color:var(--ss-green)] whitespace-pre-line leading-tight"
                    style={{ fontSize: "13px", letterSpacing: "0.18em" }}
                  >
                    {f.title}
                  </div>
                  <p
                    className="mt-3 text-[color:var(--ss-text)]/80"
                    style={{ fontSize: "12.5px", lineHeight: 1.45 }}
                  >
                    {f.desc}
                  </p>
                </Link>
              ))}
            </div>
          </HUDFrame>
        </div>
      </section>

      {/* Built for Operators banner */}
      <section className="px-6 pb-10">
        <div className="max-w-[1280px] mx-auto">
          <HUDFrame padding="p-7 md:p-10">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div
                  className="font-display font-black"
                  style={{ fontSize: "clamp(20px,2.6vw,32px)", letterSpacing: "0.05em" }}
                >
                  <span style={{ color: "#ffffff" }}>BUILT FOR OPERATORS.</span>
                </div>
                <div
                  className="font-display font-black text-green glow"
                  style={{ fontSize: "clamp(20px,2.6vw,32px)", letterSpacing: "0.05em" }}
                >
                  DRIVEN BY ARTIFICIAL INTELLIGENCE.
                </div>
                <p className="mt-4 text-[color:var(--ss-muted)] text-base md:text-lg max-w-3xl">
                  StrainScan Pro empowers operators across every vertical to
                  make data-driven decisions, ensure product quality, streamline
                  operations, and stay ahead of compliance requirements.
                </p>
              </div>
              <button
                type="button"
                onClick={onRequestDemo}
                className="hidden lg:flex items-center gap-2 text-[color:var(--ss-green)] hover:opacity-90 transition"
                aria-label="Request Demo"
                data-testid="hero-explore"
              >
                <ChevronsRight size={56} strokeWidth={2} />
                <ChevronsRight size={56} strokeWidth={2} className="-ml-8" />
              </button>
            </div>
          </HUDFrame>
        </div>
      </section>

      {/* ENTERPRISE-LEVEL footer banner */}
      <section className="px-6 pb-16">
        <div className="max-w-[1280px] mx-auto">
          <HUDFrame padding="p-5 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center">
              <div className="w-12 h-12 border border-[rgba(157,252,4,0.45)] grid place-items-center text-[color:var(--ss-green)]">
                <Lock size={22} />
              </div>
              <div className="text-center">
                <div
                  className="font-display font-bold"
                  style={{
                    color: "#ffffff",
                    fontSize: "clamp(16px,2vw,22px)",
                    letterSpacing: "0.34em",
                  }}
                >
                  ENTERPRISE-LEVEL
                </div>
                <div
                  className="font-display font-bold text-green"
                  style={{
                    fontSize: "clamp(16px,2vw,22px)",
                    letterSpacing: "0.34em",
                  }}
                >
                  PROPRIETARY SOFTWARE
                </div>
              </div>
              <div
                className="font-display font-bold text-[color:var(--ss-green)] whitespace-nowrap"
                style={{ fontSize: "clamp(14px,1.4vw,18px)", letterSpacing: "0.18em" }}
              >
                (OTC TICKER: MDCE)
              </div>
            </div>
          </HUDFrame>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-[1100px] mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-display font-black">
            Intelligence you can trust.{" "}
            <span className="text-green glow">Accuracy you can depend on.</span>
          </h2>
          <p className="mt-5 text-[color:var(--ss-muted)] text-lg max-w-2xl mx-auto">
            Deploy StrainScan Pro across your operation and unlock
            AI-powered product recognition, quality assessment, composition
            insights, and end-to-end batch tracking.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button type="button" className="btn-primary" onClick={onRequestDemo} data-testid="hero-request-demo">
              Request Demo
            </button>
            <Link to="/contact" className="btn-ghost">
              Contact Sales
            </Link>
          </div>
          <ul className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-[color:var(--ss-muted)]">
            {["High-accuracy AI", "Enterprise Security", "Compliance Ready", "Continuous Learning"].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <Check size={16} className="text-[color:var(--ss-green)]" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
